import { useState, useEffect, useCallback } from 'react';
import { db } from '../../../firebase';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from 'firebase/firestore';
import { acknowledgeAlert as acknowledgeAlertService } from '../../../services/documentService';

/**
 * Severity priority map — critical alerts surface first.
 */
const SEVERITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };

/**
 * Sort alerts: critical-first, then by createdAt descending.
 */
function sortAlerts(alerts) {
  return [...alerts].sort((a, b) => {
    const sevA = SEVERITY_ORDER[a.severity] ?? 99;
    const sevB = SEVERITY_ORDER[b.severity] ?? 99;
    if (sevA !== sevB) return sevA - sevB;
    // Both have same severity — newest first
    const tA = a.createdAt?.seconds ?? 0;
    const tB = b.createdAt?.seconds ?? 0;
    return tB - tA;
  });
}

/**
 * Hook to subscribe to active risk alerts (Hugo_RiskAlerts) for an organization.
 *
 * Acknowledgement is PER-USER: alerts whose `acknowledgedBy` array contains
 * `currentUid` are filtered out of this user's view, but stay active (and
 * visible) for everyone else in the org. Pass the signed-in user's uid to get
 * that filtering; omit it to see all active alerts regardless of who
 * acknowledged them (e.g. admin/debug views).
 *
 * @param {string|null} organizationId - The org to watch
 * @param {string|null} currentUid - Signed-in user's uid (enables per-user filtering)
 * @returns {{ alerts: Array, loading: boolean, error: Error|null, acknowledgeAlert: Function }}
 */
export function useRiskAlerts(organizationId, currentUid = null) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!organizationId) {
      setAlerts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const q = query(
      collection(db, 'Hugo_RiskAlerts'),
      where('organizationId', '==', organizationId),
      where('status', '==', 'active'),
      orderBy('createdAt', 'desc'),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const items = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          // Per-user acknowledgement: hide alerts THIS user has acknowledged.
          // (Array.isArray guard also skips legacy docs where acknowledgedBy
          // was a single-uid string under the old org-wide behavior.)
          .filter(
            (a) => !(currentUid && Array.isArray(a.acknowledgedBy) && a.acknowledgedBy.includes(currentUid))
          );
        setAlerts(sortAlerts(items));
        setLoading(false);
      },
      (err) => {
        console.error('useRiskAlerts: snapshot error', err);
        setError(err);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [organizationId, currentUid]);

  /**
   * Acknowledge (dismiss) an alert by ID.
   * @param {string} alertId
   * @param {string} uid - Current user's UID
   */
  const acknowledgeAlert = useCallback(async (alertId, uid) => {
    try {
      await acknowledgeAlertService(alertId, uid);
    } catch (err) {
      console.error('useRiskAlerts: acknowledge error', err);
      setError(err);
    }
  }, []);

  return { alerts, loading, error, acknowledgeAlert };
}

export default useRiskAlerts;
